import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Users, Heart, Star, Flame } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { CATEGORY_EMOJIS } from '../../constants/recipeConstants';

const PLACEHOLDER_COLORS = [
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
  'linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)',
  'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
  'linear-gradient(135deg, #f5576c 0%, #ff7043 100%)',
];

function getPlaceholderGradient(id) {
  return PLACEHOLDER_COLORS[(Number(id) || 0) % PLACEHOLDER_COLORS.length];
}

// Canonical emoji map — new categories added to the constants surface here automatically.
const CATEGORY_EMOJI = CATEGORY_EMOJIS;

const DIFFICULTY_BADGE = {
  easy: 'bg-emerald-500',
  medium: 'bg-amber-500',
  hard: 'bg-red-500',
};

export default function RecipeCard({ recipe }) {
  const navigate = useNavigate();
  const { toggleFavorite } = useApp();

  const handleFavorite = (e) => {
    e.stopPropagation();
    toggleFavorite(recipe.id);
  };

  const primaryCategory = recipe.categories?.[0];
  const emoji = CATEGORY_EMOJI[primaryCategory] || '🍴';
  const primaryImage = recipe.images?.find((img) => img.isPrimary) || recipe.images?.[0];
  const imageUrl = primaryImage?.url || recipe.image;
  const rating = recipe.communityRating > 0 ? recipe.communityRating : recipe.rating;

  return (
    <div
      onClick={() => navigate(`/recipes/${recipe.id}`)}
      className="bg-white dark:bg-night-surface rounded-xl shadow-sm border border-gray-100 dark:border-night-card overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
    >
      <div
        className="relative aspect-[4/3] bg-cover bg-center flex items-center justify-center"
        style={imageUrl ? { backgroundImage: `url(${imageUrl})` } : { background: getPlaceholderGradient(recipe.id) }}
      >
        {!imageUrl && <span className="text-5xl drop-shadow" aria-hidden="true">{emoji}</span>}

        <button
          onClick={handleFavorite}
          aria-label={recipe.favorite ? 'Remove from favourites' : 'Add to favourites'}
          aria-pressed={!!recipe.favorite}
          className={[
            'absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-colors',
            recipe.favorite ? 'bg-white text-red-500' : 'bg-black/40 text-white hover:bg-black/60',
          ].join(' ')}
        >
          <Heart size={18} fill={recipe.favorite ? 'currentColor' : 'none'} />
        </button>

        {rating > 0 && (
          <div className="absolute bottom-2 left-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-black/55 text-white text-xs font-semibold">
            <Star size={12} fill="currentColor" aria-hidden="true" />
            <span>{rating}</span>
          </div>
        )}

        {recipe.cuisine && (
          <span className="absolute top-2 left-2 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-black/55 text-white">
            {recipe.cuisine}
          </span>
        )}

        {recipe.difficulty && (
          <span
            className={[
              'absolute bottom-2 right-2 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full text-white',
              DIFFICULTY_BADGE[recipe.difficulty] || 'bg-gray-500',
            ].join(' ')}
          >
            {recipe.difficulty}
          </span>
        )}
      </div>

      <div className="p-3 space-y-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-night-text line-clamp-2 leading-snug">
          {recipe.title}
        </h3>

        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-night-muted">
          {recipe.totalTime > 0 && (
            <span className="inline-flex items-center gap-1">
              <Clock size={12} aria-hidden="true" />
              {recipe.totalTime} min
            </span>
          )}
          {recipe.servings > 0 && (
            <span className="inline-flex items-center gap-1">
              <Users size={12} aria-hidden="true" />
              {recipe.servings}
            </span>
          )}
          {recipe.nutrition?.calories > 0 && (
            <span className="inline-flex items-center gap-1">
              <Flame size={12} aria-hidden="true" />
              {recipe.nutrition.calories} cal
            </span>
          )}
        </div>

        {recipe.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {recipe.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-night-card text-gray-600 dark:text-night-muted"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
